// components/SituacionActual.js

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  IconButton,
  Autocomplete,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  CardActionArea,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
  Gavel as GavelIcon
} from '@mui/icons-material';
import api from '../../api/axiosConfig';
import { getMediaBaseUrl } from '../../utils/mediaUrl';

// IMPORTA TU SELECT DE SELECCIÓN NACIONAL
import SelectSelectionNational from '../SelectSelectionNational';

function SituacionActual({
  player,
  editablePlayer,
  setEditablePlayer,
  isEditing,
  isMobile,
  teams,       // array de equipos
  leagues2     // array para "Liga Secundaria"
}) {
  const { t } = useTranslation();

  // Fonction pour convertir la clé de sélection nationale en label traduit
  const getNationalTeamDisplayLabel = (selectedValue) => {
    if (!selectedValue) return '';

    // Si la valeur contient "createPlayer.countries.", c'est une ancienne donnée mal formatée
    if (selectedValue.includes('createPlayer.countries.')) {
      // Extraire le nom du pays et la catégorie depuis le format "createPlayer.countries.Angola U15"
      const withoutPrefix = selectedValue.replace('createPlayer.countries.', '');
      // Essayer de trouver une catégorie d'âge (U14, U15, etc.)
      const ageCategoryMatch = withoutPrefix.match(/\s(U\d+)$/);
      if (ageCategoryMatch) {
        const countryName = withoutPrefix.replace(/\s(U\d+)$/, '');
        return `${countryName} ${ageCategoryMatch[1]}`;
      }
      return withoutPrefix;
    }

    // Format normal: chercher si c'est une clé avec catégorie d'âge (ex: "southAfrica_U20")
    const parts = selectedValue.split('_');
    const countryKey = parts[0];
    const ageCategory = parts[1] || '';

    const translatedCountry = t(`createPlayer.countries.${countryKey}`, countryKey);
    return ageCategory ? `${translatedCountry} ${ageCategory}` : translatedCountry;
  };
  const [competitionsStructure, setCompetitionsStructure] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCompetitions, setSelectedCompetitions] = useState([]);
  
  // États pour la recherche d'équipes
  const [searchTerm, setSearchTerm] = useState('');
  const [equipesOptions, setEquipesOptions] = useState([]);
  
  // État pour le dialog de confirmation de suppression
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [competitionToDelete, setCompetitionToDelete] = useState(null);
  const [equipeProprietaire, setEquipeProprietaire] = useState(null);
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [ownerSearchTerm, setOwnerSearchTerm] = useState('');

  // États NCAA Commitment
  const [ncaaEquipesOptions, setNcaaEquipesOptions] = useState([]);
  const [ncaaCommitment, setNcaaCommitment] = useState(null);
  const [ncaaDialogOpen, setNcaaDialogOpen] = useState(false);
  const [ncaaSearchTerm, setNcaaSearchTerm] = useState('');

  // États Derecho de tanteo (équipes ACB / Segunda FEB)
  const [tanteoEquipesOptions, setTanteoEquipesOptions] = useState([]);
  const [tanteoCommitment, setTanteoCommitment] = useState(null);
  const [tanteoDialogOpen, setTanteoDialogOpen] = useState(false);
  const [tanteoSearchTerm, setTanteoSearchTerm] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const mediaUrl = getMediaBaseUrl();
  const section = process.env.REACT_APP_SECTION;

  // Engagements "effectifs" en lecture seule : depuis la 1re compétition (joueur
  // avec équipe) ou, à défaut, depuis les champs niveau joueur (cas free agent).
  const commitC0 = (player && player.competitions_detail && player.competitions_detail[0]) || {};
  const ownerTeamId = commitC0.equipe_proprietaire_id || (player && player.equipe_proprietaire_id) || null;
  const ownerTeamNom = commitC0.equipe_proprietaire_nom || (player && player.equipe_proprietaire_nom) || null;
  const ncaaTeamId = commitC0.ncaa_commitment_id || (player && player.ncaa_commitment_id) || null;
  const ncaaTeamNom = commitC0.ncaa_commitment_nom || (player && player.ncaa_commitment_nom) || null;
  const tanteoTeamId = commitC0.tanteo_id || (player && player.tanteo_id) || null;
  const tanteoTeamNom = commitC0.tanteo_nom || (player && player.tanteo_nom) || null;

  // Fonction pour obtenir l'URL du logo
  const getLogoUrl = (id, type) => {
    return `${mediaUrl}/${type}/${id}.jpg?t=${Date.now()}`;
  };

  useEffect(() => {
    // Cargar la estructura de competiciones
    const fetchCompetitionsStructure = async () => {
      try {
        const response = await api.get('/api/players/competitions/structure');
        const structure = response.data.structure || [];
        setCompetitionsStructure(structure);
        
        // Créer la liste des équipes avec leurs compétitions
        const equipesMap = {};
        structure.forEach(item => {
          if (!equipesMap[item.equipe_id]) {
            equipesMap[item.equipe_id] = {
              equipe_id: item.equipe_id,
              equipe_nom: item.equipe_nom,
              competitions: []
            };
          }
          // Ajouter la compétition si elle n'existe pas déjà
          const exists = equipesMap[item.equipe_id].competitions.find(
            c => c.competition_id === item.competition_id
          );
          if (!exists) {
            equipesMap[item.equipe_id].competitions.push({
              niveau_id: item.niveau_id,
              niveau_nom: item.niveau_nom,
              competition_id: item.competition_id,
              competition_nom: item.competition_nom
            });
          }
        });
        
        // Convertir en array et formater pour l'affichage
        const equipesArray = Object.values(equipesMap).map(equipe => ({
          ...equipe,
          display: `${equipe.equipe_nom} (${equipe.competitions.map(c => c.competition_nom).join(', ')})`
        }));
        
        setEquipesOptions(equipesArray);
      } catch (error) {
        console.error('Error fetching competitions structure:', error);
      }
    };

    // Charger les équipes NCAA uniquement (pour le sélecteur "NCAA Commitment")
    const fetchNcaaStructure = async () => {
      try {
        const response = await api.get('/api/players/competitions/structure/ncaa');
        const structure = response.data.structure || [];

        const equipesMap = {};
        structure.forEach(item => {
          if (!equipesMap[item.equipe_id]) {
            equipesMap[item.equipe_id] = {
              equipe_id: item.equipe_id,
              equipe_nom: item.equipe_nom,
              competitions: []
            };
          }
          const exists = equipesMap[item.equipe_id].competitions.find(
            c => c.competition_id === item.competition_id
          );
          if (!exists) {
            equipesMap[item.equipe_id].competitions.push({
              niveau_id: item.niveau_id,
              niveau_nom: item.niveau_nom,
              competition_id: item.competition_id,
              competition_nom: item.competition_nom
            });
          }
        });

        const equipesArray = Object.values(equipesMap).map(equipe => ({
          ...equipe,
          display: `${equipe.equipe_nom} (${equipe.competitions.map(c => c.competition_nom).join(', ')})`
        }));

        setNcaaEquipesOptions(equipesArray);
      } catch (error) {
        console.error('Error fetching NCAA structure:', error);
      }
    };

    // Charger les équipes ACB / Segunda FEB (pour le sélecteur "Derecho de tanteo")
    const fetchTanteoStructure = async () => {
      try {
        const response = await api.get('/api/players/competitions/structure/tanteo');
        const structure = response.data.structure || [];

        const equipesMap = {};
        structure.forEach(item => {
          if (!equipesMap[item.equipe_id]) {
            equipesMap[item.equipe_id] = {
              equipe_id: item.equipe_id,
              equipe_nom: item.equipe_nom,
              competitions: []
            };
          }
          const exists = equipesMap[item.equipe_id].competitions.find(
            c => c.competition_id === item.competition_id
          );
          if (!exists) {
            equipesMap[item.equipe_id].competitions.push({
              niveau_id: item.niveau_id,
              niveau_nom: item.niveau_nom,
              competition_id: item.competition_id,
              competition_nom: item.competition_nom
            });
          }
        });

        const equipesArray = Object.values(equipesMap).map(equipe => ({
          ...equipe,
          display: `${equipe.equipe_nom} (${equipe.competitions.map(c => c.competition_nom).join(', ')})`
        }));

        setTanteoEquipesOptions(equipesArray);
      } catch (error) {
        console.error('Error fetching tanteo structure:', error);
      }
    };

    if (isEditing) {
      fetchCompetitionsStructure();
      fetchNcaaStructure();
      fetchTanteoStructure();
    }
  }, [isEditing, backendUrl, section]);

  useEffect(() => {
    if (editablePlayer.competitions_detail) {
      setSelectedCompetitions(editablePlayer.competitions_detail);
    }
  }, [editablePlayer.competitions_detail]);

  // Initialiser equipeProprietaire / ncaaCommitment uniquement au chargement du joueur
  // ou quand les options de référence deviennent disponibles. On ne réinitialise PAS
  // ces valeurs quand l'utilisateur supprime des compétitions, sinon supprimer la
  // dernière ligne de competitions_detail effacerait aussi le owner/NCAA en mémoire.
  // L'utilisateur efface explicitement ces champs via les boutons X dédiés.
  useEffect(() => {
    if (!editablePlayer.competitions_detail) return;

    const firstWithOwner = editablePlayer.competitions_detail.find(c => c.equipe_proprietaire_id);
    const ownerId = (firstWithOwner && firstWithOwner.equipe_proprietaire_id) || editablePlayer.equipe_proprietaire_id || null;
    const ownerNom = (firstWithOwner && firstWithOwner.equipe_proprietaire_nom) || editablePlayer.equipe_proprietaire_nom || '';
    if (ownerId) {
      const ownerEquipe = equipesOptions.length > 0 ? equipesOptions.find(eq => eq.equipe_id === ownerId) : null;
      setEquipeProprietaire(ownerEquipe || { equipe_id: ownerId, equipe_nom: ownerNom, display: ownerNom });
    }

    const firstWithCommit = editablePlayer.competitions_detail.find(c => c.ncaa_commitment_id);
    const ncaaId = (firstWithCommit && firstWithCommit.ncaa_commitment_id) || editablePlayer.ncaa_commitment_id || null;
    const ncaaNom = (firstWithCommit && firstWithCommit.ncaa_commitment_nom) || editablePlayer.ncaa_commitment_nom || '';
    if (ncaaId) {
      const ncaaEquipe = ncaaEquipesOptions.length > 0 ? ncaaEquipesOptions.find(eq => eq.equipe_id === ncaaId) : null;
      setNcaaCommitment(ncaaEquipe || { equipe_id: ncaaId, equipe_nom: ncaaNom, display: ncaaNom });
    }

    const firstWithTanteo = editablePlayer.competitions_detail.find(c => c.tanteo_id);
    const tanteoId = (firstWithTanteo && firstWithTanteo.tanteo_id) || editablePlayer.tanteo_id || null;
    const tanteoNom = (firstWithTanteo && firstWithTanteo.tanteo_nom) || editablePlayer.tanteo_nom || '';
    if (tanteoId) {
      const tanteoEquipe = tanteoEquipesOptions.length > 0 ? tanteoEquipesOptions.find(eq => eq.equipe_id === tanteoId) : null;
      setTanteoCommitment(tanteoEquipe || { equipe_id: tanteoId, equipe_nom: tanteoNom, display: tanteoNom });
    }
  }, [editablePlayer.id, equipesOptions, ncaaEquipesOptions, tanteoEquipesOptions]);

  // Synchroniser les engagements (cedido / NCAA / tanteo) vers le niveau joueur de
  // editablePlayer. C'est la source de vérité envoyée au backend — indispensable
  // pour un free agent sans compétition (où competitions_detail reste vide).
  useEffect(() => {
    if (!isEditing) return;
    setEditablePlayer(prev => ({
      ...prev,
      equipe_proprietaire_id: equipeProprietaire ? equipeProprietaire.equipe_id : null,
      equipe_proprietaire_nom: equipeProprietaire ? equipeProprietaire.equipe_nom : null,
      ncaa_commitment_id: ncaaCommitment ? ncaaCommitment.equipe_id : null,
      ncaa_commitment_nom: ncaaCommitment ? ncaaCommitment.equipe_nom : null,
      tanteo_id: tanteoCommitment ? tanteoCommitment.equipe_id : null,
      tanteo_nom: tanteoCommitment ? tanteoCommitment.equipe_nom : null,
    }));
  }, [isEditing, equipeProprietaire, ncaaCommitment, tanteoCommitment, setEditablePlayer]);

  const handleOpenDialog = () => {
    setSearchTerm('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSearchTerm('');
  };

  const handleAddEquipe = (equipeOption) => {
    // Ajouter toutes les compétitions de cette équipe
    const newCompetitions = [];
    equipeOption.competitions.forEach(competition => {
      const newCompetition = {
        niveau_id: competition.niveau_id,
        niveau_nom: competition.niveau_nom,
        competition_id: competition.competition_id,
        competition_nom: competition.competition_nom,
        equipe_id: equipeOption.equipe_id,
        equipe_nom: equipeOption.equipe_nom
      };
      
      // Vérifier si cette combinaison existe déjà
      const exists = selectedCompetitions.find(c => 
        c.niveau_id === newCompetition.niveau_id &&
        c.competition_id === newCompetition.competition_id &&
        c.equipe_id === newCompetition.equipe_id
      );
      
      if (!exists) {
        newCompetitions.push(newCompetition);
      }
    });
    
    if (newCompetitions.length > 0) {
      setSelectedCompetitions(prev => [...prev, ...newCompetitions]);
    }
  };

  // Filtrer les équipes selon le terme de recherche
  const getFilteredEquipes = () => {
    if (!searchTerm) return equipesOptions;
    return equipesOptions.filter(equipe => 
      equipe.equipe_nom.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const handleSaveCompetitions = () => {
    // Injecter les engagements (cedido / NCAA / tanteo) dans chaque compétition
    const compsWithExtras = selectedCompetitions.map(comp => ({
      ...comp,
      equipe_proprietaire_id: equipeProprietaire ? equipeProprietaire.equipe_id : null,
      equipe_proprietaire_nom: equipeProprietaire ? equipeProprietaire.equipe_nom : null,
      ncaa_commitment_id: ncaaCommitment ? ncaaCommitment.equipe_id : null,
      ncaa_commitment_nom: ncaaCommitment ? ncaaCommitment.equipe_nom : null,
      tanteo_id: tanteoCommitment ? tanteoCommitment.equipe_id : null,
      tanteo_nom: tanteoCommitment ? tanteoCommitment.equipe_nom : null
    }));
    setSelectedCompetitions(compsWithExtras);
    setEditablePlayer(prev => ({
      ...prev,
      competitions_detail: compsWithExtras
    }));
    handleCloseDialog();
  };

  const handleRemoveCompetition = (index, e) => {
    e.stopPropagation();
    setCompetitionToDelete(index);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteCompetition = () => {
    if (competitionToDelete !== null) {
      const newCompetitions = [...selectedCompetitions];
      newCompetitions.splice(competitionToDelete, 1);
      setSelectedCompetitions(newCompetitions);
      setEditablePlayer(prev => ({
        ...prev,
        competitions_detail: newCompetitions
      }));
    }
    setDeleteDialogOpen(false);
    setCompetitionToDelete(null);
  };


  // === Render para MOBILE ===
  if (isMobile) {
    return (
      <Box
        sx={{
          mb: 2,
          backgroundColor: 'background.default'
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 'medium',
            px: 2,
            py: 1,
            backgroundColor: 'background.paper'
          }}
        >
          {"3. " + t('createPlayer.sections.currentSituation')}
        </Typography>

        <Box sx={{ p: 2 }}>
          {isEditing ? (
            <Grid container spacing={2}>
              {/* Free Agent */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!editablePlayer.is_free_agent}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setEditablePlayer((prev) => ({
                          ...prev,
                          is_free_agent: checked ? 1 : 0,
                          ...(checked ? { equipo: '' } : {})
                        }));
                      }}
                    />
                  }
                  label={t('createPlayer.fields.freeAgent')}
                />
              </Grid>

              {/* Selección Nacional */}
              <Grid item xs={12}>
                <SelectSelectionNational
                  value={editablePlayer.seleccionNacional || ''}
                  onChange={(e) =>
                    setEditablePlayer((prev) => ({
                      ...prev,
                      seleccionNacional: e.target.value
                    }))
                  }
                />
              </Grid>

              {/* Niveau/Compétition/Équipe */}
              <Grid item xs={12}>
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {t('createPlayer.fields.levelCompetitionTeam')}
                  </Typography>
                  <IconButton size="small" onClick={handleOpenDialog}>
                    <AddIcon />
                  </IconButton>
                </Box>
                
                {selectedCompetitions.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedCompetitions.map((comp, index) => (
                      <Box key={index} sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        p: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1
                      }}>
                        <Box 
                          component="img"
                          src={getLogoUrl(comp.competition_id, 'competitions')}
                          alt={comp.competition_nom}
                          sx={{
                            width: 25,
                            height: 25,
                            objectFit: 'contain',
                            mr: 1,
                            borderRadius: '2px'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <Box 
                          component="img"
                          src={getLogoUrl(comp.equipe_id, 'equipes')}
                          alt={comp.equipe_nom}
                          sx={{
                            width: 25,
                            height: 25,
                            objectFit: 'contain',
                            mr: 1,
                            borderRadius: '2px'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {comp.niveau_nom}
                          </Typography>
                          <Typography variant="body2">
                            {comp.competition_nom} - {comp.equipe_nom}
                          </Typography>
                        </Box>
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleRemoveCompetition(index, e)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t('createPlayer.fields.noCompetitions')}
                  </Typography>
                )}

                {/* Equipe propriétaire (cedido) - Mobile */}
                {(selectedCompetitions.length > 0 || equipeProprietaire || !!editablePlayer.is_free_agent) && (
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {t('createPlayer.fields.ownerTeam')}
                      </Typography>
                      {!equipeProprietaire && (
                        <IconButton size="small" onClick={() => { setOwnerSearchTerm(''); setOwnerDialogOpen(true); }}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    {equipeProprietaire ? (
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        backgroundColor: 'background.default'
                      }}>
                        <Box
                          component="img"
                          src={getLogoUrl(equipeProprietaire.equipe_id, 'equipes')}
                          alt={equipeProprietaire.equipe_nom}
                          sx={{ width: 25, height: 25, objectFit: 'contain', mr: 1, borderRadius: '2px' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Chip label={t('createPlayer.fields.ownerTeamBadge')} size="small" sx={{ height: 18, fontSize: '0.6rem', backgroundColor: '#ff9800', color: 'white', mb: 0.3 }} />
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {equipeProprietaire.equipe_nom}
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => {
                          setEquipeProprietaire(null);
                          const updatedComps = selectedCompetitions.map(comp => ({
                            ...comp, equipe_proprietaire_id: null, equipe_proprietaire_nom: null
                          }));
                          setSelectedCompetitions(updatedComps);
                          setEditablePlayer(prev => ({ ...prev, competitions_detail: updatedComps }));
                        }} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {t('createPlayer.fields.ownerTeamHelper')}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* NCAA Commitment - Mobile */}
                {(selectedCompetitions.length > 0 || ncaaCommitment || !!editablePlayer.is_free_agent) && (
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {t('createPlayer.fields.ncaaCommitment')}
                      </Typography>
                      {!ncaaCommitment && (
                        <IconButton size="small" onClick={() => { setNcaaSearchTerm(''); setNcaaDialogOpen(true); }}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    {ncaaCommitment ? (
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        backgroundColor: 'background.default'
                      }}>
                        <Box
                          component="img"
                          src={getLogoUrl(ncaaCommitment.equipe_id, 'equipes')}
                          alt={ncaaCommitment.equipe_nom}
                          sx={{ width: 25, height: 25, objectFit: 'contain', mr: 1, borderRadius: '2px' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Chip label={t('createPlayer.fields.ncaaCommitmentBadge')} size="small" sx={{ height: 18, fontSize: '0.6rem', backgroundColor: '#1976d2', color: 'white', mb: 0.3 }} />
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {ncaaCommitment.equipe_nom}
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => {
                          setNcaaCommitment(null);
                          const updatedComps = selectedCompetitions.map(comp => ({
                            ...comp, ncaa_commitment_id: null, ncaa_commitment_nom: null
                          }));
                          setSelectedCompetitions(updatedComps);
                          setEditablePlayer(prev => ({ ...prev, competitions_detail: updatedComps }));
                        }} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {t('createPlayer.fields.ncaaCommitmentHelper')}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Derecho de tanteo - Mobile */}
                {(selectedCompetitions.length > 0 || tanteoCommitment || !!editablePlayer.is_free_agent) && (
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        Derecho de tanteo
                      </Typography>
                      {!tanteoCommitment && (
                        <IconButton size="small" onClick={() => { setTanteoSearchTerm(''); setTanteoDialogOpen(true); }}>
                          <AddIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                    {tanteoCommitment ? (
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        backgroundColor: 'background.default'
                      }}>
                        <Box
                          component="img"
                          src={getLogoUrl(tanteoCommitment.equipe_id, 'equipes')}
                          alt={tanteoCommitment.equipe_nom}
                          sx={{ width: 25, height: 25, objectFit: 'contain', mr: 1, borderRadius: '2px' }}
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <Box sx={{ flex: 1 }}>
                          <Chip icon={<GavelIcon sx={{ fontSize: '0.8rem !important', color: 'white !important' }} />} label="Tanteo" size="small" sx={{ height: 18, fontSize: '0.6rem', backgroundColor: '#f9a825', color: 'white', mb: 0.3 }} />
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {tanteoCommitment.equipe_nom}
                          </Typography>
                        </Box>
                        <IconButton size="small" onClick={() => {
                          setTanteoCommitment(null);
                          const updatedComps = selectedCompetitions.map(comp => ({
                            ...comp, tanteo_id: null, tanteo_nom: null
                          }));
                          setSelectedCompetitions(updatedComps);
                          setEditablePlayer(prev => ({ ...prev, competitions_detail: updatedComps }));
                        }} color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Equipo ACB / Primera FEB con derecho de tanteo sobre el jugador
                      </Typography>
                    )}
                  </Box>
                )}
              </Grid>

              {/* Equipo Actual (Autocomplete) - Campo existente */}
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={teams}
                  inputValue={editablePlayer.equipo || ''}
                  onInputChange={(event, newInputValue) => {
                    setEditablePlayer((prev) => ({
                      ...prev,
                      equipo: newInputValue
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label={t('createPlayer.fields.currentTeam')} fullWidth />
                  )}
                />
              </Grid>

              {/* Liga Secundaria - Campo existente */}
              <Grid item xs={12}>
                <Autocomplete
                  freeSolo
                  options={leagues2}
                  inputValue={editablePlayer.liga2 || ''}
                  onInputChange={(event, newInputValue) => {
                    setEditablePlayer((prev) => ({
                      ...prev,
                      liga2: newInputValue
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Liga Secundaria" fullWidth />
                  )}
                />
              </Grid>
            </Grid>
          ) : (
            // READ ONLY (MOBILE)
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {!!player.is_free_agent && (
                <Box>
                  <Chip
                    component="a"
                    href="/competitions-vue?equipe=fa"
                    target="_blank"
                    rel="noopener noreferrer"
                    clickable
                    label={t('createPlayer.fields.freeAgentBadge')}
                    sx={{
                      backgroundColor: '#4caf50',
                      color: 'white',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: '#43a047' }
                    }}
                  />
                </Box>
              )}
              {!!player.seleccionNacional && (
                <Typography>
                  <strong>{t('createPlayer.fields.nationalTeam')}:</strong> {getNationalTeamDisplayLabel(player.seleccionNacional)}
                </Typography>
              )}
              
              {/* Afficher les nouvelles compétitions */}
              {player.competitions_detail && player.competitions_detail.length > 0 && (
                <Box>
                  <Typography sx={{ fontWeight: 'medium', mb: 0.5 }}>
                    {t('createPlayer.fields.levelCompetitionTeam')}:
                  </Typography>
                  {player.competitions_detail.map((comp, index) => (
                    <Box
                      key={index}
                      component="a"
                      href={`/competitions-vue?equipe=${comp.equipe_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        textDecoration: 'none',
                        color: 'inherit',
                        ml: 2,
                        p: 1,
                        borderRadius: 1,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        }
                      }}
                    >
                      <Box 
                        component="img"
                        src={getLogoUrl(comp.competition_id, 'competitions')}
                        alt={comp.competition_nom}
                        sx={{
                          width: 20,
                          height: 20,
                          objectFit: 'contain',
                          mr: 0.5,
                          borderRadius: '2px'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <Box 
                        component="img"
                        src={getLogoUrl(comp.equipe_id, 'equipes')}
                        alt={comp.equipe_nom}
                        sx={{
                          width: 20,
                          height: 20,
                          objectFit: 'contain',
                          mr: 1,
                          borderRadius: '2px'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <Typography variant="body2">
                        {comp.niveau_nom} / {comp.competition_nom} / {comp.equipe_nom}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Afficher l'équipe propriétaire en lecture seule - Mobile */}
              {ownerTeamId && (
                <Box
                  component="a"
                  href={`/competitions-vue?equipe=${ownerTeamId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1,
                    border: '2px solid #ff9800',
                    borderRadius: 1,
                    backgroundColor: '#fff8e1',
                    mt: 1,
                    textDecoration: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#fff3cd' }
                  }}>
                  <Box
                    component="img"
                    src={getLogoUrl(ownerTeamId, 'equipes')}
                    alt={ownerTeamNom}
                    sx={{ width: 25, height: 25, objectFit: 'contain', mr: 1, borderRadius: '2px' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
                    {ownerTeamNom}
                  </Typography>
                  <Chip label={t('createPlayer.fields.ownerTeamBadge')} size="small" sx={{ backgroundColor: '#ff9800', color: 'white', fontWeight: 'bold', height: 20, fontSize: '0.7rem' }} />
                </Box>
              )}

              {/* Afficher le commitment NCAA en lecture seule - Mobile */}
              {ncaaTeamId && (
                <Box
                  component="a"
                  href={`/competitions-vue?equipe=${ncaaTeamId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1,
                    border: '2px solid #1976d2',
                    borderRadius: 1,
                    backgroundColor: '#e3f2fd',
                    mt: 1,
                    textDecoration: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#bbdefb' }
                  }}>
                  <Box
                    component="img"
                    src={getLogoUrl(ncaaTeamId, 'equipes')}
                    alt={ncaaTeamNom}
                    sx={{ width: 25, height: 25, objectFit: 'contain', mr: 1, borderRadius: '2px' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
                    {ncaaTeamNom}
                  </Typography>
                  <Chip label={t('createPlayer.fields.ncaaCommitmentBadge')} size="small" sx={{ backgroundColor: '#1976d2', color: 'white', fontWeight: 'bold', height: 20, fontSize: '0.7rem' }} />
                </Box>
              )}

              {/* Afficher le derecho de tanteo en lecture seule - Mobile */}
              {tanteoTeamId && (
                <Box
                  component="a"
                  href={`/competitions-vue?equipe=${tanteoTeamId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1,
                    border: '2px solid #f9a825',
                    borderRadius: 1,
                    backgroundColor: '#fff8e1',
                    mt: 1,
                    textDecoration: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#ffecb3' }
                  }}>
                  <Box
                    component="img"
                    src={getLogoUrl(tanteoTeamId, 'equipes')}
                    alt={tanteoTeamNom}
                    sx={{ width: 25, height: 25, objectFit: 'contain', mr: 1, borderRadius: '2px' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
                    {tanteoTeamNom}
                  </Typography>
                  <Chip icon={<GavelIcon sx={{ fontSize: '0.8rem !important', color: 'white !important' }} />} label="Tanteo" size="small" sx={{ backgroundColor: '#f9a825', color: 'white', fontWeight: 'bold', height: 20, fontSize: '0.7rem' }} />
                </Box>
              )}

              {/* Afficher les anciens champs s'ils existent */}
              {!!player.equipo && (
                <Typography>
                  <strong>{t('createPlayer.fields.currentTeam')}:</strong> {player.equipo}
                </Typography>
              )}
              {!!player.liga2 && (
                <Typography>
                  <strong>Liga Secundaria:</strong> {player.liga2}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Dialog pour sélection des compétitions avec filtre */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} fullScreen>
          <DialogTitle>
            <Typography variant="h6">{t('common.select')} {t('players.team')}</Typography>
            <TextField
              fullWidth
              placeholder={t('common.search') + " " + t('players.team').toLowerCase() + "..."}
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mt: 2 }}
              size="small"
            />
          </DialogTitle>
          <DialogContent>
            <List sx={{ pt: 0 }}>
              {getFilteredEquipes().map(equipeOption => {
                // Vérifier si toutes les compétitions de cette équipe sont déjà sélectionnées
                const allSelected = equipeOption.competitions.every(competition => 
                  selectedCompetitions.find(c => 
                    c.niveau_id === competition.niveau_id &&
                    c.competition_id === competition.competition_id &&
                    c.equipe_id === equipeOption.equipe_id
                  )
                );
                
                return (
                  <ListItemButton
                    key={equipeOption.equipe_id}
                    onClick={() => !allSelected && handleAddEquipe(equipeOption)}
                    disabled={allSelected}
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      px: 1,
                      py: 0.5,
                      minHeight: '48px',
                      '&:hover': {
                        backgroundColor: allSelected ? 'transparent' : 'action.hover'
                      },
                      cursor: allSelected ? 'default' : 'pointer'
                    }}
                  >
                    <Box 
                      component="img"
                      src={getLogoUrl(equipeOption.equipe_id, 'equipes')}
                      alt={equipeOption.equipe_nom}
                      sx={{
                        width: 32,
                        height: 32,
                        objectFit: 'contain',
                        mr: 1.5,
                        borderRadius: '4px'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <ListItemText 
                      primary={equipeOption.display}
                      primaryTypographyProps={{ 
                        fontSize: '0.875rem',
                        sx: { 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          pr: 1
                        }
                      }}
                    />
                    {allSelected ? (
                      <Chip label="✓" size="small" color="success" />
                    ) : (
                      <AddIcon fontSize="small" color="primary" />
                    )}
                  </ListItemButton>
                );
              })}
            </List>

            {/* Afficher les compétitions sélectionnées */}
            {selectedCompetitions.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('createPlayer.dialogs.selectedCompetitions')} ({selectedCompetitions.length}):
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {selectedCompetitions.map((comp, index) => (
                    <Chip
                      key={index}
                      label={`${comp.niveau_nom} - ${comp.competition_nom} - ${comp.equipe_nom}`}
                      onDelete={() => {
                        const newComps = [...selectedCompetitions];
                        newComps.splice(index, 1);
                        setSelectedCompetitions(newComps);
                      }}
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
            <Button
              onClick={handleSaveCompetitions}
              variant="contained"
              disabled={selectedCompetitions.length === 0}
            >
              {t('common.save')} ({selectedCompetitions.length})
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de confirmation de suppression */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>{t('createPlayer.dialogs.confirmDelete')}</DialogTitle>
          <DialogContent>
            <Typography>
              {t('createPlayer.dialogs.confirmDeleteMessage')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={confirmDeleteCompetition} color="error" variant="contained">
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  // === Render para DESKTOP ===
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 3,
        backgroundColor: 'background.paper',
        boxShadow: '0 1px 3px rgba(16,24,40,0.04)'
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          fontSize: '1rem',
          fontWeight: 600,
          color: 'text.primary',
          letterSpacing: '-0.01em',
          mb: 1,
          pb: 0.5,
          borderBottom: '1px solid',
          borderColor: 'primary.main'
        }}
      >
        {"3. " + t('createPlayer.sections.currentSituation')}
      </Typography>

      {isEditing ? (
        <Grid container spacing={2}>
          {/* Free Agent */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!editablePlayer.is_free_agent}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setEditablePlayer((prev) => ({
                      ...prev,
                      is_free_agent: checked ? 1 : 0,
                      ...(checked ? { equipo: '' } : {})
                    }));
                  }}
                />
              }
              label={t('createPlayer.fields.freeAgent')}
            />
          </Grid>

          {/* Selección Nacional */}
          <Grid item xs={12} md={4}>
            <SelectSelectionNational
              value={editablePlayer.seleccionNacional || ''}
              onChange={(e) =>
                setEditablePlayer((prev) => ({
                  ...prev,
                  seleccionNacional: e.target.value
                }))
              }
            />
          </Grid>

          {/* Niveau/Compétition/Équipe */}
          <Grid item xs={12}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2
            }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                {t('createPlayer.fields.levelCompetitionTeam')}
              </Typography>
              <Button
                startIcon={<AddIcon />}
                variant="outlined"
                size="small"
                onClick={handleOpenDialog}
              >
                {t('common.add')}
              </Button>
            </Box>
            
            {selectedCompetitions.length > 0 ? (
              <Grid container spacing={1}>
                {selectedCompetitions.map((comp, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      p: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      backgroundColor: 'background.default'
                    }}>
                      <Box 
                        component="img"
                        src={getLogoUrl(comp.competition_id, 'competitions')}
                        alt={comp.competition_nom}
                        sx={{
                          width: 35,
                          height: 35,
                          objectFit: 'contain',
                          mr: 1.5,
                          borderRadius: '4px'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <Box 
                        component="img"
                        src={getLogoUrl(comp.equipe_id, 'equipes')}
                        alt={comp.equipe_nom}
                        sx={{
                          width: 35,
                          height: 35,
                          objectFit: 'contain',
                          mr: 1.5,
                          borderRadius: '4px'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Chip 
                          label={comp.niveau_nom} 
                          size="small" 
                          sx={{ 
                            backgroundColor: 'primary.main',
                            color: 'white',
                            mb: 0.5
                          }} 
                        />
                        <Typography variant="body2">
                          {comp.competition_nom}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {comp.equipe_nom}
                        </Typography>
                      </Box>
                      <IconButton 
                        size="small" 
                        onClick={(e) => handleRemoveCompetition(index, e)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                {t('createPlayer.fields.noCompetitions')}
              </Typography>
            )}

            {/* Equipe propriétaire (cedido) - Desktop */}
            {(selectedCompetitions.length > 0 || equipeProprietaire || !!editablePlayer.is_free_agent) && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                    {t('createPlayer.fields.ownerTeam')}
                  </Typography>
                  {!equipeProprietaire && (
                    <Button
                      startIcon={<AddIcon />}
                      variant="outlined"
                      size="small"
                      onClick={() => { setOwnerSearchTerm(''); setOwnerDialogOpen(true); }}
                    >
                      {t('common.add')}
                    </Button>
                  )}
                </Box>
                {equipeProprietaire ? (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'background.default',
                    cursor: 'pointer'
                  }}
                  onClick={() => { setOwnerSearchTerm(''); setOwnerDialogOpen(true); }}
                  >
                    <Box
                      component="img"
                      src={getLogoUrl(equipeProprietaire.equipe_id, 'equipes')}
                      alt={equipeProprietaire.equipe_nom}
                      sx={{ width: 35, height: 35, objectFit: 'contain', mr: 1.5, borderRadius: '4px' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Chip
                        label={t('createPlayer.fields.ownerTeamBadge')}
                        size="small"
                        sx={{ backgroundColor: '#ff9800', color: 'white', mb: 0.5 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {equipeProprietaire.equipe_nom}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {equipeProprietaire.competitions?.map(c => c.competition_nom).join(', ')}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEquipeProprietaire(null);
                        const updatedComps = selectedCompetitions.map(comp => ({
                          ...comp, equipe_proprietaire_id: null, equipe_proprietaire_nom: null
                        }));
                        setSelectedCompetitions(updatedComps);
                        setEditablePlayer(prev => ({ ...prev, competitions_detail: updatedComps }));
                      }}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    {t('createPlayer.fields.ownerTeamHelper')}
                  </Typography>
                )}
              </Box>
            )}

            {/* NCAA Commitment - Desktop */}
            {(selectedCompetitions.length > 0 || ncaaCommitment || !!editablePlayer.is_free_agent) && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                    {t('createPlayer.fields.ncaaCommitment')}
                  </Typography>
                  {!ncaaCommitment && (
                    <Button
                      startIcon={<AddIcon />}
                      variant="outlined"
                      size="small"
                      onClick={() => { setNcaaSearchTerm(''); setNcaaDialogOpen(true); }}
                    >
                      {t('common.add')}
                    </Button>
                  )}
                </Box>
                {ncaaCommitment ? (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'background.default',
                    cursor: 'pointer'
                  }}
                  onClick={() => { setNcaaSearchTerm(''); setNcaaDialogOpen(true); }}
                  >
                    <Box
                      component="img"
                      src={getLogoUrl(ncaaCommitment.equipe_id, 'equipes')}
                      alt={ncaaCommitment.equipe_nom}
                      sx={{ width: 35, height: 35, objectFit: 'contain', mr: 1.5, borderRadius: '4px' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Chip
                        label={t('createPlayer.fields.ncaaCommitmentBadge')}
                        size="small"
                        sx={{ backgroundColor: '#1976d2', color: 'white', mb: 0.5 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {ncaaCommitment.equipe_nom}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {ncaaCommitment.competitions?.map(c => c.competition_nom).join(', ')}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setNcaaCommitment(null);
                        const updatedComps = selectedCompetitions.map(comp => ({
                          ...comp, ncaa_commitment_id: null, ncaa_commitment_nom: null
                        }));
                        setSelectedCompetitions(updatedComps);
                        setEditablePlayer(prev => ({ ...prev, competitions_detail: updatedComps }));
                      }}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    {t('createPlayer.fields.ncaaCommitmentHelper')}
                  </Typography>
                )}
              </Box>
            )}

            {/* Derecho de tanteo - Desktop */}
            {(selectedCompetitions.length > 0 || tanteoCommitment || !!editablePlayer.is_free_agent) && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2
                }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                    Derecho de tanteo
                  </Typography>
                  {!tanteoCommitment && (
                    <Button
                      startIcon={<AddIcon />}
                      variant="outlined"
                      size="small"
                      onClick={() => { setTanteoSearchTerm(''); setTanteoDialogOpen(true); }}
                    >
                      {t('common.add')}
                    </Button>
                  )}
                </Box>
                {tanteoCommitment ? (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    backgroundColor: 'background.default',
                    cursor: 'pointer'
                  }}
                  onClick={() => { setTanteoSearchTerm(''); setTanteoDialogOpen(true); }}
                  >
                    <Box
                      component="img"
                      src={getLogoUrl(tanteoCommitment.equipe_id, 'equipes')}
                      alt={tanteoCommitment.equipe_nom}
                      sx={{ width: 35, height: 35, objectFit: 'contain', mr: 1.5, borderRadius: '4px' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Chip
                        icon={<GavelIcon sx={{ fontSize: '0.9rem !important', color: 'white !important' }} />}
                        label="Tanteo"
                        size="small"
                        sx={{ backgroundColor: '#f9a825', color: 'white', mb: 0.5 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {tanteoCommitment.equipe_nom}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {tanteoCommitment.competitions?.map(c => c.competition_nom).join(', ')}
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTanteoCommitment(null);
                        const updatedComps = selectedCompetitions.map(comp => ({
                          ...comp, tanteo_id: null, tanteo_nom: null
                        }));
                        setSelectedCompetitions(updatedComps);
                        setEditablePlayer(prev => ({ ...prev, competitions_detail: updatedComps }));
                      }}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                    Equipo ACB / Primera FEB con derecho de tanteo sobre el jugador
                  </Typography>
                )}
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
          </Grid>

          {/* Equipo Actual (Autocomplete) - Campo existente */}
          <Grid item xs={12} md={4}>
            <Autocomplete
              freeSolo
              options={teams}
              inputValue={editablePlayer.equipo || ''}
              onInputChange={(event, newInputValue) => {
                setEditablePlayer((prev) => ({
                  ...prev,
                  equipo: newInputValue
                }));
              }}
              renderInput={(params) => (
                <TextField {...params} label={t('createPlayer.fields.currentTeam')} fullWidth />
              )}
            />
          </Grid>

          {/* Liga Secundaria - Campo existente */}
          <Grid item xs={12} md={4}>
            <Autocomplete
              freeSolo
              options={leagues2}
              inputValue={editablePlayer.liga2 || ''}
              onInputChange={(event, newInputValue) => {
                setEditablePlayer((prev) => ({
                  ...prev,
                  liga2: newInputValue
                }));
              }}
              renderInput={(params) => (
                <TextField {...params} label="Liga Secundaria" fullWidth />
              )}
            />
          </Grid>
        </Grid>
      ) : (
        // READ ONLY (DESKTOP)
        <Box sx={{ mt: 0.5 }}>
          <Grid container rowSpacing={1} columnSpacing={2.5}>
            {!!player.is_free_agent && (
              <Grid item xs={12}>
                <Chip
                  component="a"
                  href="/competitions-vue?equipe=fa"
                  target="_blank"
                  rel="noopener noreferrer"
                  clickable
                  label={t('createPlayer.fields.freeAgentBadge')}
                  sx={{
                    backgroundColor: '#4caf50',
                    color: 'white',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#43a047' }
                  }}
                />
              </Grid>
            )}
            {!!player.seleccionNacional && (
              <Grid item xs={12} sm={6} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.03em'
                    }}
                  >
                    {t('createPlayer.fields.nationalTeam')}:
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      fontWeight: 400
                    }}
                  >
                    {getNationalTeamDisplayLabel(player.seleccionNacional)}
                  </Typography>
                </Box>
              </Grid>
            )}
          
          {/* Afficher les nouvelles compétitions */}
          {player.competitions_detail && player.competitions_detail.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em',
                    minWidth: 'auto',
                    flexShrink: 0
                  }}
                >
                  {t('createPlayer.fields.levelCompetitionTeam')}:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {player.competitions_detail.map((comp, index) => (
                    <Box
                      key={index}
                      component="a"
                      href={`/competitions-vue?equipe=${comp.equipe_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover': {
                          '& .competition-text': {
                            color: 'primary.main',
                            textDecoration: 'underline'
                          }
                        }
                      }}
                    >
                      <Box
                        component="img"
                        src={getLogoUrl(comp.competition_id, 'competitions')}
                        alt={comp.competition_nom}
                        sx={{
                          width: 20,
                          height: 20,
                          objectFit: 'contain',
                          mr: 0.5,
                          borderRadius: '2px'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <Box
                        component="img"
                        src={getLogoUrl(comp.equipe_id, 'equipes')}
                        alt={comp.equipe_nom}
                        sx={{
                          width: 20,
                          height: 20,
                          objectFit: 'contain',
                          mr: 0.75,
                          borderRadius: '2px'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <Typography
                        className="competition-text"
                        sx={{
                          fontSize: '0.95rem',
                          color: 'text.primary',
                          fontWeight: 400
                        }}
                      >
                        {comp.niveau_nom} / {comp.competition_nom} / {comp.equipe_nom}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          )}

          {/* Afficher l'équipe propriétaire en lecture seule */}
          {ownerTeamId && (
            <Grid item xs={12}>
              <Box
                component="a"
                href={`/competitions-vue?equipe=${ownerTeamId}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  border: '2px solid #ff9800',
                  borderRadius: 1,
                  backgroundColor: '#fff8e1',
                  mt: 1,
                  textDecoration: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#fff3cd' }
                }}>
                <Box
                  component="img"
                  src={getLogoUrl(ownerTeamId, 'equipes')}
                  alt={ownerTeamNom}
                  sx={{ width: 25, height: 25, objectFit: 'contain', mr: 1, borderRadius: '2px' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
                  {ownerTeamNom}
                </Typography>
                <Chip label={t('createPlayer.fields.ownerTeamBadge')} size="small" sx={{ backgroundColor: '#ff9800', color: 'white', fontWeight: 'bold', height: 20, fontSize: '0.7rem' }} />
              </Box>
            </Grid>
          )}

          {/* Afficher le commitment NCAA en lecture seule - Desktop */}
          {ncaaTeamId && (
            <Grid item xs={12}>
              <Box
                component="a"
                href={`/competitions-vue?equipe=${ncaaTeamId}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  border: '2px solid #1976d2',
                  borderRadius: 1,
                  backgroundColor: '#e3f2fd',
                  mt: 1,
                  textDecoration: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#bbdefb' }
                }}>
                <Box
                  component="img"
                  src={getLogoUrl(ncaaTeamId, 'equipes')}
                  alt={ncaaTeamNom}
                  sx={{ width: 25, height: 25, objectFit: 'contain', mr: 1, borderRadius: '2px' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
                  {ncaaTeamNom}
                </Typography>
                <Chip label={t('createPlayer.fields.ncaaCommitmentBadge')} size="small" sx={{ backgroundColor: '#1976d2', color: 'white', fontWeight: 'bold', height: 20, fontSize: '0.7rem' }} />
              </Box>
            </Grid>
          )}

          {/* Afficher le derecho de tanteo en lecture seule - Desktop */}
          {tanteoTeamId && (
            <Grid item xs={12}>
              <Box
                component="a"
                href={`/competitions-vue?equipe=${tanteoTeamId}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1,
                  border: '2px solid #f9a825',
                  borderRadius: 1,
                  backgroundColor: '#fff8e1',
                  mt: 1,
                  textDecoration: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#ffecb3' }
                }}>
                <Box
                  component="img"
                  src={getLogoUrl(tanteoTeamId, 'equipes')}
                  alt={tanteoTeamNom}
                  sx={{ width: 25, height: 25, objectFit: 'contain', mr: 1, borderRadius: '2px' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <Typography variant="body2" sx={{ fontWeight: 'bold', mr: 1 }}>
                  {tanteoTeamNom}
                </Typography>
                <Chip icon={<GavelIcon sx={{ fontSize: '0.9rem !important', color: 'white !important' }} />} label="Tanteo" size="small" sx={{ backgroundColor: '#f9a825', color: 'white', fontWeight: 'bold', height: 20, fontSize: '0.7rem' }} />
              </Box>
            </Grid>
          )}

          {/* Afficher les anciens champs s'ils existent */}
          {(!!player.equipo || !!player.liga2) && (
            <>
              <Divider sx={{ my: 2 }} />
            </>
          )}
          
          {!!player.equipo && (
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  {t('createPlayer.fields.currentTeam')}:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    color: 'text.primary',
                    fontWeight: 400
                  }}
                >
                  {player.equipo}
                </Typography>
              </Box>
            </Grid>
          )}
          {!!player.liga2 && (
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', columnGap: 1, rowGap: 0.25 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.03em'
                  }}
                >
                  Liga Secundaria:
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.95rem',
                    color: 'text.primary',
                    fontWeight: 400
                  }}
                >
                  {player.liga2}
                </Typography>
              </Box>
            </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* Dialog pour sélection des compétitions avec filtre (Desktop) */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h6">{t('common.select')} {t('players.team')}</Typography>
          <TextField
            fullWidth
            placeholder={t('common.search') + " " + t('players.team').toLowerCase() + "..."}
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            <List dense>
              {getFilteredEquipes().map(equipeOption => {
                // Vérifier si toutes les compétitions de cette équipe sont déjà sélectionnées
                const allSelected = equipeOption.competitions.every(competition => 
                  selectedCompetitions.find(c => 
                    c.niveau_id === competition.niveau_id &&
                    c.competition_id === competition.competition_id &&
                    c.equipe_id === equipeOption.equipe_id
                  )
                );
                
                return (
                  <ListItemButton
                    key={equipeOption.equipe_id}
                    onClick={() => !allSelected && handleAddEquipe(equipeOption)}
                    disabled={allSelected}
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      py: 1,
                      '&:hover': {
                        backgroundColor: allSelected ? 'transparent' : 'action.hover'
                      },
                      cursor: allSelected ? 'default' : 'pointer'
                    }}
                  >
                    <Box 
                      component="img"
                      src={getLogoUrl(equipeOption.equipe_id, 'equipes')}
                      alt={equipeOption.equipe_nom}
                      sx={{
                        width: 40,
                        height: 40,
                        objectFit: 'contain',
                        mr: 2,
                        borderRadius: '4px'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <ListItemText 
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {equipeOption.equipe_nom}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ({equipeOption.competitions.map(c => c.competition_nom).join(', ')})
                          </Typography>
                        </Box>
                      }
                    />
                    {allSelected ? (
                      <Chip label="✓" size="small" color="success" />
                    ) : (
                      <AddIcon color="primary" />
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          </Box>

          {/* Afficher les compétitions sélectionnées */}
          {selectedCompetitions.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'medium' }}>
                {t('createPlayer.dialogs.selectedCompetitions')} ({selectedCompetitions.length}):
              </Typography>
              <Grid container spacing={1}>
                {selectedCompetitions.map((comp, index) => (
                  <Grid item key={index}>
                    <Chip
                      label={`${comp.niveau_nom} - ${comp.competition_nom} - ${comp.equipe_nom}`}
                      onDelete={() => {
                        const newComps = [...selectedCompetitions];
                        newComps.splice(index, 1);
                        setSelectedCompetitions(newComps);
                      }}
                      variant="outlined"
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSaveCompetitions}
            variant="contained"
            disabled={selectedCompetitions.length === 0}
          >
            {t('common.save')} ({selectedCompetitions.length})
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmation de suppression */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('createPlayer.dialogs.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('createPlayer.dialogs.confirmDeleteMessage')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={confirmDeleteCompetition} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour sélection de l'équipe propriétaire */}
      <Dialog open={ownerDialogOpen} onClose={() => setOwnerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6">{t('createPlayer.fields.ownerTeam')}</Typography>
          <TextField
            fullWidth
            placeholder={t('createPlayer.dialogs.searchTeam')}
            variant="outlined"
            value={ownerSearchTerm}
            onChange={(e) => setOwnerSearchTerm(e.target.value)}
            sx={{ mt: 2 }}
            size="small"
          />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            <List dense>
              {equipesOptions
                .filter(eq => !selectedCompetitions.some(c => c.equipe_id === eq.equipe_id))
                .filter(eq => !ownerSearchTerm || eq.equipe_nom.toLowerCase().includes(ownerSearchTerm.toLowerCase()))
                .map(equipeOption => (
                  <ListItemButton
                    key={equipeOption.equipe_id}
                    onClick={() => {
                      const newValue = equipeOption;
                      setEquipeProprietaire(newValue);
                      const updatedComps = selectedCompetitions.map(comp => ({
                        ...comp,
                        equipe_proprietaire_id: newValue.equipe_id,
                        equipe_proprietaire_nom: newValue.equipe_nom
                      }));
                      setSelectedCompetitions(updatedComps);
                      setEditablePlayer(prev => ({ ...prev, competitions_detail: updatedComps }));
                      setOwnerDialogOpen(false);
                    }}
                    selected={equipeProprietaire?.equipe_id === equipeOption.equipe_id}
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      py: 1
                    }}
                  >
                    <Box
                      component="img"
                      src={getLogoUrl(equipeOption.equipe_id, 'equipes')}
                      alt={equipeOption.equipe_nom}
                      sx={{ width: 40, height: 40, objectFit: 'contain', mr: 2, borderRadius: '4px' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {equipeOption.equipe_nom}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ({equipeOption.competitions.map(c => c.competition_nom).join(', ')})
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOwnerDialogOpen(false)}>{t('common.cancel')}</Button>
          {equipeProprietaire && (
            <Button onClick={() => {
              setEquipeProprietaire(null);
              const updatedComps = selectedCompetitions.map(comp => ({
                ...comp, equipe_proprietaire_id: null, equipe_proprietaire_nom: null
              }));
              setSelectedCompetitions(updatedComps);
              setEditablePlayer(prev => ({ ...prev, competitions_detail: updatedComps }));
              setOwnerDialogOpen(false);
            }} color="error">
              {t('common.delete')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog pour sélection du commitment NCAA */}
      <Dialog open={ncaaDialogOpen} onClose={() => setNcaaDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6">{t('createPlayer.fields.ncaaCommitment')}</Typography>
          <TextField
            fullWidth
            placeholder={t('createPlayer.dialogs.searchTeam')}
            variant="outlined"
            value={ncaaSearchTerm}
            onChange={(e) => setNcaaSearchTerm(e.target.value)}
            sx={{ mt: 2 }}
            size="small"
          />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            <List dense>
              {ncaaEquipesOptions
                .filter(eq => !ncaaSearchTerm || eq.equipe_nom.toLowerCase().includes(ncaaSearchTerm.toLowerCase()))
                .map(equipeOption => (
                  <ListItemButton
                    key={equipeOption.equipe_id}
                    onClick={() => {
                      const newValue = equipeOption;
                      setNcaaCommitment(newValue);
                      const updatedComps = selectedCompetitions.map(comp => ({
                        ...comp,
                        ncaa_commitment_id: newValue.equipe_id,
                        ncaa_commitment_nom: newValue.equipe_nom
                      }));
                      setSelectedCompetitions(updatedComps);
                      setEditablePlayer(prev => ({ ...prev, competitions_detail: updatedComps }));
                      setNcaaDialogOpen(false);
                    }}
                    selected={ncaaCommitment?.equipe_id === equipeOption.equipe_id}
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      py: 1
                    }}
                  >
                    <Box
                      component="img"
                      src={getLogoUrl(equipeOption.equipe_id, 'equipes')}
                      alt={equipeOption.equipe_nom}
                      sx={{ width: 40, height: 40, objectFit: 'contain', mr: 2, borderRadius: '4px' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {equipeOption.equipe_nom}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ({equipeOption.competitions.map(c => c.competition_nom).join(', ')})
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNcaaDialogOpen(false)}>{t('common.cancel')}</Button>
          {ncaaCommitment && (
            <Button onClick={() => {
              setNcaaCommitment(null);
              const updatedComps = selectedCompetitions.map(comp => ({
                ...comp, ncaa_commitment_id: null, ncaa_commitment_nom: null
              }));
              setSelectedCompetitions(updatedComps);
              setEditablePlayer(prev => ({ ...prev, competitions_detail: updatedComps }));
              setNcaaDialogOpen(false);
            }} color="error">
              {t('common.delete')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog pour sélection du derecho de tanteo */}
      <Dialog open={tanteoDialogOpen} onClose={() => setTanteoDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Derecho de tanteo</Typography>
          <TextField
            fullWidth
            placeholder={t('createPlayer.dialogs.searchTeam')}
            variant="outlined"
            value={tanteoSearchTerm}
            onChange={(e) => setTanteoSearchTerm(e.target.value)}
            sx={{ mt: 2 }}
            size="small"
          />
        </DialogTitle>
        <DialogContent>
          <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
            <List dense>
              {tanteoEquipesOptions
                .filter(eq => !tanteoSearchTerm || eq.equipe_nom.toLowerCase().includes(tanteoSearchTerm.toLowerCase()))
                .map(equipeOption => (
                  <ListItemButton
                    key={equipeOption.equipe_id}
                    onClick={() => {
                      const newValue = equipeOption;
                      setTanteoCommitment(newValue);
                      const updatedComps = selectedCompetitions.map(comp => ({
                        ...comp,
                        tanteo_id: newValue.equipe_id,
                        tanteo_nom: newValue.equipe_nom
                      }));
                      setSelectedCompetitions(updatedComps);
                      setEditablePlayer(prev => ({ ...prev, competitions_detail: updatedComps }));
                      setTanteoDialogOpen(false);
                    }}
                    selected={tanteoCommitment?.equipe_id === equipeOption.equipe_id}
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      py: 1
                    }}
                  >
                    <Box
                      component="img"
                      src={getLogoUrl(equipeOption.equipe_id, 'equipes')}
                      alt={equipeOption.equipe_nom}
                      sx={{ width: 40, height: 40, objectFit: 'contain', mr: 2, borderRadius: '4px' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {equipeOption.equipe_nom}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ({equipeOption.competitions.map(c => c.competition_nom).join(', ')})
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTanteoDialogOpen(false)}>{t('common.cancel')}</Button>
          {tanteoCommitment && (
            <Button onClick={() => {
              setTanteoCommitment(null);
              const updatedComps = selectedCompetitions.map(comp => ({
                ...comp, tanteo_id: null, tanteo_nom: null
              }));
              setSelectedCompetitions(updatedComps);
              setEditablePlayer(prev => ({ ...prev, competitions_detail: updatedComps }));
              setTanteoDialogOpen(false);
            }} color="error">
              {t('common.delete')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default SituacionActual;